import { makeQr } from "@/lib/intake/qr"

// Printable QR pieces for the consumer intake link — a 5×7in counter card
// (landscape, sits by the register) and a Letter poster (waiting-room wall).
// Generated fully client-side; jsPDF is lazy-loaded so Settings doesn't pay
// for it until someone actually prints (pattern:
// onboarding-activation/signed-pdf.ts). Helvetica only — it's what jsPDF
// ships and what a shop printer won't mangle.

export type IntakePdfVariant = "card" | "poster"

export interface IntakePdfInput {
  variant: IntakePdfVariant
  shopName: string
  // Shown as the scannable fallback under the QR ("or visit …").
  shortUrl: string
  // Encoded in the QR — carries ?src=qr for attribution.
  qrUrl: string
  phone: string | null
  hours: string
  primaryColor: string
  logoUrl: string | null
  logoInitials: string
  slug: string
}

const INK: [number, number, number] = [12, 32, 24] // #0C2018
const BODY: [number, number, number] = [74, 90, 81]
const MUTED: [number, number, number] = [130, 142, 136]
const EMERALD: [number, number, number] = [5, 150, 105]

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return EMERALD
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

// Fetch the shop logo into a PNG data URL. Any failure (404, CORS taint on
// toDataURL, not an image) → null, and the brand chip falls back to initials.
async function loadLogo(url: string): Promise<string | null> {
  try {
    const img = new Image()
    img.crossOrigin = "anonymous"
    const loaded = await new Promise<HTMLImageElement>((resolve, reject) => {
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error("logo load failed"))
      img.src = url
    })
    if (!loaded.naturalWidth || !loaded.naturalHeight) return null
    const canvas = document.createElement("canvas")
    canvas.width = loaded.naturalWidth
    canvas.height = loaded.naturalHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) return null
    ctx.drawImage(loaded, 0, 0)
    return canvas.toDataURL("image/png")
  } catch {
    return null
  }
}

type Doc = import("jspdf").jsPDF

// Brand chip: the logo when it survives CORS, else a primaryColor tile with
// the shop's initials — same fallback the on-screen ShopHeader uses.
function drawChip(
  doc: Doc,
  x: number,
  y: number,
  size: number,
  brand: [number, number, number],
  initials: string,
  logo: string | null,
) {
  if (logo) {
    doc.addImage(logo, "PNG", x, y, size, size)
    return
  }
  doc.setFillColor(...brand)
  doc.roundedRect(x, y, size, size, size * 0.22, size * 0.22, "F")
  doc.setFont("helvetica", "bold").setFontSize(size * 0.42).setTextColor(255, 255, 255)
  doc.text(initials.slice(0, 2).toUpperCase(), x + size / 2, y + size / 2, {
    align: "center",
    baseline: "middle",
  })
}

// QR with a white mat + hairline so it stays scannable on any card stock.
function drawQr(doc: Doc, qrPng: string, x: number, y: number, size: number) {
  const pad = size * 0.06
  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(213, 221, 216)
  doc.setLineWidth(0.75)
  doc.roundedRect(x - pad, y - pad, size + pad * 2, size + pad * 2, 6, 6, "FD")
  doc.addImage(qrPng, "PNG", x, y, size, size)
}

export async function downloadIntakePdf(input: IntakePdfInput): Promise<void> {
  const { jsPDF } = await import("jspdf")
  // ≥1024px so a print shop can blow the poster up without the modules
  // going soft.
  const [qrPng, logo] = await Promise.all([
    makeQr(input.qrUrl, { size: 1024 }),
    input.logoUrl ? loadLogo(input.logoUrl) : Promise.resolve(null),
  ])
  const brand = hexToRgb(input.primaryColor)

  const doc =
    input.variant === "card"
      ? new jsPDF({ unit: "pt", format: [504, 360], orientation: "landscape" })
      : new jsPDF({ unit: "pt", format: "letter" })

  if (input.variant === "card") {
    drawCard(doc, input, brand, qrPng, logo)
  } else {
    drawPoster(doc, input, brand, qrPng, logo)
  }

  const kind = input.variant === "card" ? "counter-card" : "poster"
  doc.save(`${input.slug}-intake-${kind}.pdf`)
}

// 5×7in landscape: copy on the left, QR on the right — reads like a table
// tent panel.
function drawCard(
  doc: Doc,
  input: IntakePdfInput,
  brand: [number, number, number],
  qrPng: string,
  logo: string | null,
) {
  const W = 504
  const H = 360
  const M = 36

  // brand hairline across the top
  doc.setFillColor(...brand)
  doc.rect(0, 0, W, 4, "F")

  drawChip(doc, M, M, 40, brand, input.logoInitials, logo)
  doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(...INK)
  doc.text(doc.splitTextToSize(input.shopName, 200) as string[], M + 52, M + 16)

  const colW = 250
  let y = 128
  doc.setFont("helvetica", "bold").setFontSize(23).setTextColor(...INK)
  doc.text(doc.splitTextToSize("Scan to get your estimate started", colW) as string[], M, y, {
    lineHeightFactor: 1.15,
  })
  y += 62
  doc.setFont("helvetica", "normal").setFontSize(10.5).setTextColor(...BODY)
  doc.text(
    doc.splitTextToSize(
      "A few photos of the damage is all it takes — free, no obligation.",
      colW,
    ) as string[],
    M,
    y,
    { lineHeightFactor: 1.35 },
  )

  // or-visit URL
  doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(...EMERALD)
  doc.text(doc.splitTextToSize(`or visit ${input.shortUrl}`, colW) as string[], M, 244)

  // footer: hours · phone
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...MUTED)
  const footer = [input.hours, input.phone].filter(Boolean).join("   ·   ")
  if (footer) doc.text(footer, M, H - M + 6)

  drawQr(doc, qrPng, W - M - 168, (H - 168) / 2, 168)
}

// Letter poster: everything centered, QR as the hero.
function drawPoster(
  doc: Doc,
  input: IntakePdfInput,
  brand: [number, number, number],
  qrPng: string,
  logo: string | null,
) {
  const W = 612
  const H = 792
  const CX = W / 2

  doc.setFillColor(...brand)
  doc.rect(0, 0, W, 6, "F")

  drawChip(doc, CX - 26, 64, 52, brand, input.logoInitials, logo)
  doc.setFont("helvetica", "bold").setFontSize(16).setTextColor(...INK)
  doc.text(input.shopName, CX, 148, { align: "center" })

  doc.setFont("helvetica", "bold").setFontSize(31).setTextColor(...INK)
  doc.text(
    doc.splitTextToSize("Scan to get your estimate started", 440) as string[],
    CX,
    206,
    { align: "center", lineHeightFactor: 1.15 },
  )

  doc.setFont("helvetica", "normal").setFontSize(12.5).setTextColor(...BODY)
  doc.text(
    doc.splitTextToSize(
      "Send us a few photos of the damage — free, no obligation.",
      420,
    ) as string[],
    CX,
    268,
    { align: "center", lineHeightFactor: 1.35 },
  )

  const qrSize = 300
  drawQr(doc, qrPng, CX - qrSize / 2, 316, qrSize)

  doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(...EMERALD)
  doc.text(`or visit ${input.shortUrl}`, CX, 672, { align: "center" })

  doc.setFont("helvetica", "normal").setFontSize(10.5).setTextColor(...MUTED)
  const footer = [input.hours, input.phone].filter(Boolean).join("   ·   ")
  if (footer) doc.text(footer, CX, 720, { align: "center" })
}
